import { useSite } from "@inrange/building-manager-api-client";
import { Icons } from "@inrange/theme-components/icons";
import { Nav, NavDropdown } from "react-bootstrap";
import { Link, useLocation, useParams } from "react-router-dom";
import Loading from "../../Loading";
import { OPERATIONAL_STATUSES } from "../operationalStatusUtils";

type NavItem = { path: string; label: string; external?: boolean };

const SiteNav = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const { fetchSite } = useSite({
    siteId,
    app: "admin",
    enableNonEssentialQueries: false,
  });
  const location = useLocation();

  const site = fetchSite.data?.site;
  if (!fetchSite.isLoading && fetchSite.isError) {
    return <div>Error loading the site.</div>;
  }
  if (fetchSite.isLoading || !site) {
    return <Loading label="Loading site data..." />;
  }

  const buyMatches = site.sdmMatches.filter(
    (match) => match.buyerId === site.id
  );
  const sellMatches = site.sdmMatches.filter(
    (match) => match.sellerId === site.id
  );
  const siteFiles = site.siteFiles.filter(
    (file) => file.fileType !== "pvDesignImage"
  );

  const navItems: NavItem[] = [
    {
      path: `/site/${siteId}/edit`,
      label: `${site.locked ? "🔒 " : ""}Site Data`,
    },
    {
      path: `/site/${siteId}/scenarios`,
      label: `Scenarios (${site.nearbySiteIDs.length})`,
    },
    {
      path: `/site/${siteId}/sdm`,
      label: `SDM (${sellMatches.length} sell, ${buyMatches.length} buy)`,
    },
    {
      path: `/site/${siteId}/filesreports`,
      label: `Files (${siteFiles.length}) & Reports`,
    },
  ];

  // Procurement-only sites have no on-site PV, so Performance + Health
  // (which centre on generation, PR and PV-system availability) have nothing
  // meaningful to show.
  const isProcurementOnly = site.totalInstalledCapacity === 0;
  if (
    !isProcurementOnly &&
    (OPERATIONAL_STATUSES.has(site.operationalStatus) ||
      site.operationalStatus === "operationalDemo")
  ) {
    navItems.push({ path: `/site/${siteId}/perf`, label: "Performance" });
    navItems.push({ path: `/site/${siteId}/health`, label: "Health" });
  }

  navItems.push({
    path: `/site/${siteId}/pvreport`,
    label: "PV Report",
    external: true,
  });

  const activeNavPath =
    navItems.find(
      (item) =>
        location.pathname === item.path ||
        location.pathname.startsWith(item.path + "/")
    )?.path ?? location.pathname;

  const externalLinkLabel = (label: string) => (
    <>
      {label}
      <img
        alt="view"
        src={Icons.shareBlue}
        style={{
          width: "17px",
          height: "17px",
          marginLeft: "8px",
          marginBottom: "2px",
        }}
      />
    </>
  );

  return (
    <>
      <style>
        {`
          #site-nav-dropdown + .dropdown-menu {
            z-index: 20000 !important;
          }
        `}
      </style>
      {/* Desktop nav tabs */}
      <Nav
        activeKey={activeNavPath}
        className="d-none d-md-flex"
        style={{
          padding: "8px 12px",
          backgroundColor: "#d9d9d9",
          zIndex: 1000,
        }}
        aria-label="Site navigation"
      >
        {navItems.map((item) => (
          <Nav.Item key={item.path}>
            {item.external ? (
              <Nav.Link
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
              >
                {externalLinkLabel(item.label)}
              </Nav.Link>
            ) : (
              <Nav.Link
                eventKey={item.path}
                as={Link}
                to={{ pathname: item.path }}
              >
                {item.label}
              </Nav.Link>
            )}
          </Nav.Item>
        ))}
      </Nav>
      {/* Mobile dropdown */}
      <Nav
        className="d-md-none"
        style={{
          padding: "8px 12px",
          backgroundColor: "#d9d9d9",
          zIndex: 20000,
        }}
        aria-hidden="true"
      >
        <NavDropdown
          title={
            navItems.find((item) => item.path === activeNavPath)?.label ||
            "Navigation"
          }
          id="site-nav-dropdown"
        >
          {navItems.map((item) =>
            item.external ? (
              <NavDropdown.Item
                key={item.path}
                href={item.path}
                target="_blank"
                rel="noopener noreferrer"
              >
                {externalLinkLabel(item.label)}
              </NavDropdown.Item>
            ) : (
              <NavDropdown.Item
                key={item.path}
                as={Link}
                to={item.path}
                active={activeNavPath === item.path}
              >
                {item.label}
              </NavDropdown.Item>
            )
          )}
        </NavDropdown>
      </Nav>
    </>
  );
};

export default SiteNav;
